import { useKeyboardEventContext } from "providers/keyboard-event-provider/KeyboardEventProvider";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { sandboxApi } from "services/sandbox-api/api";
import { sourceFilesTransformBack } from "services/sandbox-api/transforms";
import { sandboxSaved } from "store/features";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { toaster } from "@itwin/itwinui-react";
import { QueryReturnValue } from "@reduxjs/toolkit/dist/query/baseQueryTypes";
import { useDebounce } from "./useDebounce";
import { SourceFile } from "@sandbox/code-editor";
import { sampleUserId } from "configuration";

interface SaveControllerOptions {
  children: React.ReactNode
}

const autoSaveEnabled = localStorage.getItem("autoSave") || "true" === "true";

export const SaveController = ({ children }: SaveControllerOptions) => {
  const dispatch = useAppDispatch();
  const sandboxMetadata = useAppSelector((state) => state.activeSandbox.metadata);
  const changedFiles = useAppSelector((state) => state.activeSandbox.changedFiles);
  const sourceFiles = useAppSelector((state) => state.activeSandbox.sourceFiles);
  const authUser = useAppSelector((state) => state.auth.user);
  const [updateSandbox] = sandboxApi.useCodeShareUpdateFilesMutation();
  const [manualSave, setManualSave] = useState(false);
  const showSaveAlert = useRef(true);

  const { registerEvent } = useKeyboardEventContext();

  const immediateUpdate = useCallback(() => {
    if (!sandboxMetadata)
      return;

    let payload: SourceFile[] = [];
    let needsUpdate = false;
    for (let i = 0; i < sourceFiles.length; i++) {
      if (sourceFiles[i].deleted) {
        needsUpdate = true;
        continue;
      }

      if (sourceFiles[i].filePathRenamed) {
        const renamed = changedFiles.find((item) => item.filePathRenamed === sourceFiles[i].filePathRenamed);
        renamed ? payload.push(renamed) : payload.push(sourceFiles[i]);
        needsUpdate = true;
      }

      const changed = changedFiles.find((item) => item.filePath === sourceFiles[i].filePath);
      if (changed) {
        payload.push(changed)
        needsUpdate = true;
      } else {
        payload.push(sourceFiles[i]);
      }
    }

    if (needsUpdate) {
      updateSandbox({
        id: sandboxMetadata.id,
        userId: sandboxMetadata.userId,
        files: sourceFilesTransformBack(payload)
      }).then((response: QueryReturnValue) => {
        if (response.error)
          console.log(response.error)
        else {
          dispatch(sandboxSaved());
          if (manualSave && showSaveAlert.current) {
            toaster.closeAll();
            toaster.positive("Saved!", { hasCloseButton: true, duration: 3000, link: { title: "Don't show again", onClick: () => { toaster.closeAll(); showSaveAlert.current = false; } } });
          }
        }
      });
    }
  }, [sandboxMetadata, changedFiles, sourceFiles, updateSandbox, manualSave]);

  const debouncedUpdate = useDebounce(immediateUpdate);

  useEffect(() => {
    // Sandbox not opened or user is not signed-in
    if (!sandboxMetadata || !authUser)
      return;

    // Signed in user is not owner of the sandbox
    if (authUser.id !== sandboxMetadata.userId) {
      if (process.env.NODE_ENV === "development" && sandboxMetadata.userId === sampleUserId) {
        // allow modify Sample Sandboxes in development environment
      } else {
        return;
      }
    }

    if (autoSaveEnabled)
      debouncedUpdate();
  }, [sandboxMetadata, changedFiles, authUser]);

  useEffect(() => {
    if (manualSave) {
      immediateUpdate();
      setManualSave(false);
    }
  }, [manualSave]);

  useEffect(() => {
    const unregister = registerEvent({ ctrlKey: true, key: "s" }, () => setManualSave(true));
    return () => {
      unregister();
    };
  }, [registerEvent]);

  return <>{children}</>
}